import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Users, Lock, Unlock, ArrowLeft, Crown, Zap, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { goBackOrHome } from '../../lib/navigation';
import { useUserStore } from '../../lib/userStore';
import { useLanguageStore } from '../../lib/languageStore';
import { useThemeStore } from '../../lib/themeStore';
import { translate } from '../../lib/translations';
import { sendJoinRequestNotificationEmail, getCommissionerEmail, getLeagueInfo } from '../../lib/api/emailNotifications';
import type { LeagueType } from '../../lib/types/leagueMode';

interface League {
  league_id: number;
  name: string;
  created_at: string;
  is_private: boolean;
  league_type: string;
}

export function JoinLeague() {
  const navigate = useNavigate();
  const [availableLeagues, setAvailableLeagues] = useState<League[]>([]);
  const [loading, setLoading] = useState(false);
  const [joiningLeague, setJoiningLeague] = useState<number | null>(null);
  const [leagueTypeFilter, setLeagueTypeFilter] = useState<LeagueType | null>(null);
  const [privacyFilter, setPrivacyFilter] = useState<'all' | 'public' | 'private'>('all');
  const username = useUserStore((state) => state.username);
  const userId = useUserStore((state) => state.user_id);
  const { language } = useLanguageStore();
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const cardBg = isDark ? 'rgba(15,23,42,0.95)' : '#fff';

  useEffect(() => {
    if (!username) {
      navigate('/');
      return;
    }
    if (!leagueTypeFilter) return;

    async function fetchAvailableLeagues() {
      setLoading(true);
      setAvailableLeagues([]);
      try {
        // Get user ID from the users table
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('user_id')
          .eq('username', username)
          .single();

        if (userError) throw userError;
        if (!userData) throw new Error('User not found');

        // Get the leagues the user is already a member of
        const { data: userLeagues } = await supabase
          .from('user_leagues')
          .select('league_id')
          .eq('user_id', userData.user_id);

        const userLeagueIds = userLeagues?.map(ul => ul.league_id) || [];

        // Fetch only leagues of the selected type
        const { data: leagues, error: leaguesError } = await supabase
          .from('leagues')
          .select('league_id, name, created_at, is_private, league_type')
          .not('league_id', 'in', userLeagueIds.length > 0 ? `(${userLeagueIds.join(',')})` : '(0)')
          .eq('league_type', leagueTypeFilter)
          .order('created_at', { ascending: false });

        if (leaguesError) throw leaguesError;

        setAvailableLeagues(leagues || []);
      } catch (error: any) {
        console.error('Error:', error);
        toast.error(translate('failed.load.leagues', language));
      } finally {
        setLoading(false);
      }
    }

    fetchAvailableLeagues();
  }, [username, navigate, language, leagueTypeFilter]);

  const handleSelectType = (type: LeagueType) => {
    setLeagueTypeFilter(type);
    setPrivacyFilter('all');
  };

  const handleBack = () => {
    setLeagueTypeFilter(null);
    setAvailableLeagues([]);
    setPrivacyFilter('all');
  };

  const handleJoinLeague = async (leagueId: number, leagueName: string, isPrivate: boolean) => {
    if (!username || !userId) {
      navigate('/');
      return;
    }

    try {
      setJoiningLeague(leagueId);

      if (isPrivate) {
        // Insert into league_invitation table
        const { error: invitationError } = await supabase
          .from('league_invitation')
          .insert({
            league_id: leagueId,
            user_id: Number(userId),
            username: username
          });

        if (invitationError) throw invitationError;

        // Envoyer l'email de notification au commissaire
        try {
          console.log('🔍 [JOIN] Starting email notification process...');
          console.log('🔍 [JOIN] League ID:', leagueId);
          console.log('🔍 [JOIN] League Name:', leagueName);
          console.log('🔍 [JOIN] Requester Username:', username);
          
          const [commissionerInfo, leagueInfo] = await Promise.all([
            getCommissionerEmail(leagueId),
            getLeagueInfo(leagueId)
          ]);

          console.log('📊 [JOIN] Commissioner info result:', JSON.stringify(commissionerInfo, null, 2));
          console.log('📊 [JOIN] League info result:', JSON.stringify(leagueInfo, null, 2));

          if (commissionerInfo.error) {
            console.error('❌ [JOIN] Error getting commissioner info:', commissionerInfo.error);
            toast.error(`Erreur lors de la récupération des infos du commissaire: ${commissionerInfo.error}`);
          }

          if (leagueInfo.error) {
            console.error('❌ [JOIN] Error getting league info:', leagueInfo.error);
            toast.error(`Erreur lors de la récupération des infos de la ligue: ${leagueInfo.error}`);
          }

          if (commissionerInfo.email && commissionerInfo.username) {
            const emailData = {
              commissionerEmail: commissionerInfo.email,
              commissionerUsername: commissionerInfo.username,
              requesterUsername: username,
              leagueName: leagueInfo.name
            };

            console.log('📤 [JOIN] Preparing to send email with data:', JSON.stringify(emailData, null, 2));
            const emailResult = await sendJoinRequestNotificationEmail(emailData);
            
            console.log('📬 [JOIN] Email result:', JSON.stringify(emailResult, null, 2));
            
            if (emailResult.success) {
              console.log('✅ [JOIN] Join request notification email sent successfully!');
              toast.success('Email de notification envoyé au commissaire!');
            } else {
              console.error('❌ [JOIN] Failed to send join request notification email:', emailResult.error);
              toast.error(`Échec de l'envoi de l'email: ${emailResult.error}`);
            }
          } else {
            const missingInfo = [];
            if (!commissionerInfo.email) missingInfo.push('email');
            if (!commissionerInfo.username) missingInfo.push('username');
            
            console.error('❌ [JOIN] Missing commissioner information:', {
              email: commissionerInfo.email,
              username: commissionerInfo.username,
              missing: missingInfo
            });
            toast.error(`Informations du commissaire manquantes: ${missingInfo.join(', ')}`);
          }
        } catch (emailError: any) {
          console.error('❌ [JOIN] Exception in email notification process:', emailError);
          console.error('❌ [JOIN] Error stack:', emailError.stack);
          toast.error(`Erreur lors de l'envoi de l'email: ${emailError.message}`);
          // On ne fait pas échouer la demande si l'email échoue
        }

        toast.success(translate('request.sent', language, { name: leagueName }));
        navigate('/dashboard');
        return;
      }

      // For public leagues, use add_user_to_league function
      const { error: joinError } = await supabase.rpc('add_user_to_league', {
        p_user_id: Number(userId),
        p_league_id: leagueId
      });

      if (joinError) {
        if (joinError.message.includes('duplicate key value')) {
          toast.error(translate('already.member', language, { name: leagueName }));
          setAvailableLeagues(prevLeagues => 
            prevLeagues.filter(league => league.league_id !== leagueId)
          );
          return;
        }
        throw joinError;
      }
      
      toast.success(translate('joined.success', language, { name: leagueName }));

      // Notifier le commissaire qu'un nouveau membre a rejoint (non-bloquant)
      try {
        const [commissionerInfo, leagueInfo] = await Promise.all([
          getCommissionerEmail(leagueId),
          getLeagueInfo(leagueId),
        ]);
        if (commissionerInfo.email && commissionerInfo.username) {
          await sendJoinRequestNotificationEmail({
            commissionerEmail: commissionerInfo.email,
            commissionerUsername: commissionerInfo.username,
            requesterUsername: username,
            leagueName: leagueInfo.name || leagueName,
          });
        }
      } catch (emailErr) {
        console.error('Commissioner join notification failed (non-blocking):', emailErr);
      }

      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error:', error);
      toast.error(translate('failed.join', language));
    } finally {
      setJoiningLeague(null);
    }
  };

  const filteredLeagues = availableLeagues.filter(league => {
    if (privacyFilter === 'public') return !league.is_private;
    if (privacyFilter === 'private') return league.is_private;
    return true;
  });

  return (
    <div className="card shadow-lg border-0">
      <div className="card-body p-5">
        <button
          className="btn btn-link text-primary mb-4 p-0 d-flex align-items-center"
          onClick={leagueTypeFilter ? handleBack : () => goBackOrHome(navigate)}
        >
          <ArrowLeft size={20} className="me-2" />
          {leagueTypeFilter ? 'Choisir un autre type de ligue' : translate('back', language)}
        </button>

        <div className="text-center mb-4">
          <Users className="h-12 w-12 text-primary mb-2" />
          <h2 className="card-title text-center mb-1">{translate('join.league', language)}</h2>
          {leagueTypeFilter && (
            <p className="text-muted small">
              Étape 2 / 2 — {leagueTypeFilter === 'dynasty' ? 'Dynasty / Keeper' : 'Standard Flexible'}
            </p>
          )}
        </div>

        {/* Progress bar */}
        <div className="progress mb-4" style={{ height: 6 }}>
          <div
            className="progress-bar bg-primary"
            style={{ width: leagueTypeFilter ? '100%' : '50%', transition: 'width 0.3s ease' }}
          />
        </div>

        {/* ── Étape 1 : Choisir le type de ligue ── */}
        {!leagueTypeFilter && (
          <div>
            <p className="text-center text-muted mb-4">
              Choisissez le type de ligue que vous souhaitez rejoindre.
            </p>
            <div className="row g-3">
              {/* Dynasty / Keeper */}
              <div className="col-12 col-md-6">
                <button
                  type="button"
                  onClick={() => handleSelectType('dynasty')}
                  className="w-100 h-100 border-0 text-start p-0"
                  style={{ background: 'none', cursor: 'pointer' }}
                >
                  <div
                    className="card h-100"
                    style={{
                      background: cardBg,
                      border: isDark ? '1px solid rgba(59,130,246,0.2)' : '1px solid #dee2e6',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = '#0d6efd')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = isDark ? 'rgba(59,130,246,0.2)' : '#dee2e6')}
                  >
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: 40, height: 40, background: 'rgba(13,110,253,0.1)' }}
                        >
                          <Crown size={20} className="text-primary" />
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">Dynasty / Keeper</h6>
                          <div className="d-flex align-items-center gap-1 flex-wrap mt-1">
                            <span className="badge bg-primary" style={{ fontSize: '0.7rem' }}>Mode classique</span>
                            <span className="badge bg-warning text-dark" style={{ fontSize: '0.7rem' }}>5,50 $ / usager</span>
                          </div>
                        </div>
                      </div>
                      <ul className="list-unstyled mb-0 small" style={{ color: isDark ? '#94a3b8' : '#6c757d' }}>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🔁</span> Pool à vie, multi-saisons</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🔒</span> Joueurs exclusifs par ligue</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>⚙️</span> Draft serpentin ou linéaire</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🔄</span> Système d'échanges complet</li>
                        <li className="d-flex align-items-start gap-2"><span>🛡️</span> Liste de protection et keeper</li>
                      </ul>
                    </div>
                  </div>
                </button>
              </div>

              {/* Standard Flexible */}
              <div className="col-12 col-md-6">
                <button
                  type="button"
                  onClick={() => handleSelectType('standard_flexible')}
                  className="w-100 h-100 border-0 text-start p-0"
                  style={{ background: 'none', cursor: 'pointer' }}
                >
                  <div
                    className="card h-100"
                    style={{
                      background: cardBg,
                      border: isDark ? '1px solid rgba(59,130,246,0.2)' : '1px solid #dee2e6',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = '#198754')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = isDark ? 'rgba(59,130,246,0.2)' : '#dee2e6')}
                  >
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: 40, height: 40, background: 'rgba(25,135,84,0.1)' }}
                        >
                          <Zap size={20} className="text-success" />
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">Standard Flexible</h6>
                          <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>Nouveau · Gratuit</span>
                        </div>
                      </div>
                      <ul className="list-unstyled mb-0 small" style={{ color: isDark ? '#94a3b8' : '#6c757d' }}>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🔁</span> Pour une seule saison</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🌐</span> Joueurs partagés entre équipes</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🏗️</span> Construction libre d'équipe</li>
                        <li className="d-flex align-items-start gap-2 mb-1"><span>🚫</span> Pas de draft ni d'échanges</li>
                        <li className="d-flex align-items-start gap-2"><span>✅</span> Toujours gratuit</li>
                      </ul>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Étape 2 : Liste des ligues du type choisi ── */}
        {leagueTypeFilter && (
          <div>
            {/* Badge type sélectionné */}
            <div className="d-flex align-items-center justify-content-center gap-2 mb-4">
              {leagueTypeFilter === 'dynasty' ? (
                <Crown size={18} className="text-primary" />
              ) : (
                <Zap size={18} className="text-success" />
              )}
              <span className={`fw-bold ${leagueTypeFilter === 'dynasty' ? 'text-primary' : 'text-success'}`}>
                {leagueTypeFilter === 'dynasty' ? 'Dynasty / Keeper' : 'Standard Flexible'}
              </span>
            </div>

            {/* Filtres public / privé */}
            <div className="d-flex flex-wrap gap-2 justify-content-center mb-4">
              <button
                className={`btn btn-sm ${privacyFilter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setPrivacyFilter('all')}
              >
                Toutes ({availableLeagues.length})
              </button>
              <button
                className={`btn btn-sm ${privacyFilter === 'public' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => setPrivacyFilter('public')}
              >
                <Unlock size={14} className="me-1" />
                Publiques ({availableLeagues.filter(l => !l.is_private).length})
              </button>
              <button
                className={`btn btn-sm ${privacyFilter === 'private' ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={() => setPrivacyFilter('private')}
              >
                <Lock size={14} className="me-1" />
                Privées ({availableLeagues.filter(l => l.is_private).length})
              </button>
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">{translate('loading', language)}</span>
                </div>
                <p className="text-muted mt-2 small">Chargement des ligues...</p>
              </div>
            ) : availableLeagues.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted mb-4">
                  Aucune ligue{' '}
                  <strong>{leagueTypeFilter === 'dynasty' ? 'Dynasty / Keeper' : 'Standard Flexible'}</strong>{' '}
                  disponible à rejoindre.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/create-league')}
                >
                  {translate('create.league.button', language)}
                </button>
              </div>
            ) : filteredLeagues.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">
                  {privacyFilter === 'public' && 'Aucune ligue publique disponible.'}
                  {privacyFilter === 'private' && 'Aucune ligue privée disponible.'}
                </p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {filteredLeagues.map((league) => (
                  <div
                    key={league.league_id}
                    className={`card border ${league.is_private ? 'border-warning' : 'border-success'}`}
                  >
                    <div className="card-body">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 gap-md-0">
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <h5 className="card-title mb-0">{league.name}</h5>
                            {league.is_private ? (
                              <Lock size={16} className="text-warning" />
                            ) : (
                              <Unlock size={16} className="text-success" />
                            )}
                          </div>
                          <small className="text-muted d-block">
                            {translate('created.on', language, {
                              date: new Date(league.created_at).toLocaleDateString(
                                language === 'fr' ? 'fr-FR' : 'en-US'
                              )
                            })}
                          </small>
                          <small className={league.is_private ? 'text-warning' : 'text-success'}>
                            {translate(league.is_private ? 'private.league' : 'public.league', language)}
                          </small>
                        </div>
                        <button
                          className={`btn w-100 w-md-auto mt-3 mt-md-0 ${league.is_private ? 'btn-outline-warning' : 'btn-primary'}`}
                          style={{ maxWidth: '260px' }}
                          onClick={() => handleJoinLeague(league.league_id, league.name, league.is_private)}
                          disabled={joiningLeague === league.league_id}
                        >
                          {joiningLeague === league.league_id ? (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                          ) : null}
                          {league.is_private ? translate('send.request', language) : translate('join.league', language)}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}